import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiCalendar, FiUsers, FiUser, FiMail, FiPhone, FiMessageSquare } from 'react-icons/fi';

export default function Booking() {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', adults: 2, children: 0, travelDate: '', specialRequests: '' });
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios.get(`/api/tours?_id=${tourId}`)
      .then(r => {
        const found = r.data.find(t => t._id === tourId);
        setTour(found);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tourId]);

  const price = tour ? (tour.discountedPrice || tour.price) : 0;
  const total = price * (parseInt(form.adults) + parseInt(form.children) * 0.5);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tour) return;
    setSubmitting(true);
    try {
      await axios.post('/api/bookings', { ...form, tour: tourId });
      setSuccess(true);
      toast.success('Booking submitted! We will contact you shortly.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ paddingTop: 120, textAlign: 'center', color: '#6b7280' }}>Loading...</div>;

  if (success) return (
    <div style={{ paddingTop: 120, textAlign: 'center', padding: '120px 20px' }}>
      <div style={{ fontSize: '5rem', marginBottom: 20 }}>✅</div>
      <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', marginBottom: 12, color: '#1a6b5a' }}>Booking Confirmed!</h2>
      <p style={{ color: '#6b7280', maxWidth: 480, margin: '0 auto 32px', lineHeight: 1.7 }}>
        Thank you! Your booking request has been received. Our team will contact you at <strong>{form.email}</strong> within 24 hours to confirm your tour.
      </p>
      <button onClick={() => navigate('/tours')} className="btn btn-primary" style={{ margin: '0 8px' }}>Browse More Tours</button>
      <button onClick={() => navigate('/')} className="btn btn-outline" style={{ margin: '0 8px' }}>Go Home</button>
    </div>
  );

  return (
    <div style={{ paddingTop: 72, background: '#fefaf4', minHeight: '100vh' }}>
      <div style={{ background: 'linear-gradient(135deg, #1a6b5a, #0d2a22)', padding: '50px 20px', textAlign: 'center' }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '2.5rem', color: 'white', marginBottom: 8 }}>Book Your Tour</h1>
        {tour && <p style={{ color: 'rgba(255,255,255,0.7)' }}>{tour.title}</p>}
      </div>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '50px 20px', display: 'grid', gridTemplateColumns: '1fr 340px', gap: 36, alignItems: 'start' }}>
        {/* Form */}
        <form onSubmit={handleSubmit} style={{ background: 'white', borderRadius: 16, padding: 36, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', marginBottom: 28 }}>Your Details</h3>

          <div className="grid-2">
            <div className="form-group">
              <label><FiUser size={14} style={{ marginRight: 6 }} />Full Name *</label>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your full name" />
            </div>
            <div className="form-group">
              <label><FiMail size={14} style={{ marginRight: 6 }} />Email *</label>
              <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" />
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label><FiPhone size={14} style={{ marginRight: 6 }} />Phone *</label>
              <input required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91 XXXXX XXXXX" />
            </div>
            <div className="form-group">
              <label><FiCalendar size={14} style={{ marginRight: 6 }} />Travel Date *</label>
              <input required type="date" value={form.travelDate} min={new Date().toISOString().split('T')[0]} onChange={e => setForm({ ...form, travelDate: e.target.value })} />
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label><FiUsers size={14} style={{ marginRight: 6 }} />Adults *</label>
              <input type="number" min="1" max="20" required value={form.adults} onChange={e => setForm({ ...form, adults: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Children (below 12)</label>
              <input type="number" min="0" max="10" value={form.children} onChange={e => setForm({ ...form, children: e.target.value })} />
            </div>
          </div>

          <div className="form-group">
            <label><FiMessageSquare size={14} style={{ marginRight: 6 }} />Special Requests</label>
            <textarea rows={4} value={form.specialRequests} onChange={e => setForm({ ...form, specialRequests: e.target.value })} placeholder="Any dietary requirements, accessibility needs, or special requests..." />
          </div>

          <button type="submit" disabled={submitting} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '14px', fontSize: '1rem' }}>
            {submitting ? 'Submitting...' : '🎯 Confirm Booking Request'}
          </button>
        </form>

        {/* Summary */}
        {tour && (
          <div>
            <div style={{ background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', marginBottom: 16 }}>
              <h4 style={{ fontFamily: 'Playfair Display, serif', marginBottom: 16 }}>Booking Summary</h4>
              <img src={tour.coverImage || 'https://images.unsplash.com/photo-1715457573748-8e8a70b2c1be?w=400'} alt={tour.title} style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 10, marginBottom: 14 }} />
              <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, marginBottom: 8 }}>{tour.title}</p>
              <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: 16 }}>⏱ {tour.duration}</p>
              <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', marginBottom: 8 }}>
                  <span>{form.adults} Adult{form.adults > 1 ? 's' : ''} × ₹{price.toLocaleString()}</span>
                  <span>₹{(price * parseInt(form.adults)).toLocaleString()}</span>
                </div>
                {parseInt(form.children) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', marginBottom: 8, color: '#6b7280' }}>
                    <span>{form.children} Child × ₹{(price * 0.5).toLocaleString()}</span>
                    <span>₹{(price * 0.5 * parseInt(form.children)).toLocaleString()}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1.1rem', color: '#1a6b5a', borderTop: '1px solid #f3f4f6', paddingTop: 12 }}>
                  <span>Total</span>
                  <span>₹{total.toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div style={{ background: '#f0faf7', borderRadius: 12, padding: 16, fontSize: '0.82rem', color: '#374151', lineHeight: 1.8 }}>
              💡 <strong>Note:</strong> This is an enquiry booking. Payment is collected after our team contacts you and confirms availability.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
